import { DataType, FinalData } from "../types/types";

const SUPPORTED_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
  "application/pdf",
];

export const MAX_HEIC_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB
export const MAX_HEIC_SIZE_LABEL = "25 Mo";

export class FileTooBigError extends Error {
  constructor(public maxBytes: number, public maxLabel: string) {
    super(`File exceeds ${maxLabel}`);
    this.name = "FileTooBigError";
  }
}

export class HeicConversionError extends Error {
  constructor(public originalError?: unknown) {
    super("HEIC conversion failed");
    this.name = "HeicConversionError";
  }
}

const isFileSupported = (file: File): boolean => {
  if (SUPPORTED_TYPES.includes(file.type)) return true;
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  return ["png", "jpg", "jpeg", "gif", "webp", "heic", "heif", "pdf"].includes(ext);
};

const isHeic = (file: Blob): boolean => {
  const type = (file.type || "").toLowerCase();
  if (type === "image/heic" || type === "image/heif") return true;
  const name = (file as File).name;
  if (name) {
    const ext = name.split(".").pop()?.toLowerCase() || "";
    if (ext === "heic" || ext === "heif") return true;
  }
  return false;
};

const convertHeicToJpeg = async (file: Blob): Promise<Blob> => {
  const { default: heic2any } = await import("heic2any");
  const result = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
  return Array.isArray(result) ? result[0] : result;
};

const readAsDataURL = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error || new Error("FileReader error"));
    reader.readAsDataURL(blob);
  });
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Image load failed"));
    image.src = src;
  });
};

const resizeImageIfNeeded = async (
  dataUrl: string
): Promise<string> => {
  const maxSize = 1300;
  let image: HTMLImageElement;
  try {
    image = await loadImage(dataUrl);
  } catch {
    return dataUrl;
  }

  const initialWidth = image.width;
  const initialHeight = image.height;
  let finalWidth: number;
  let finalHeight: number;

  if (initialWidth > initialHeight) {
    if (initialWidth > maxSize) {
      finalWidth = maxSize;
      finalHeight = (finalWidth * initialHeight) / initialWidth;
    } else {
      return dataUrl;
    }
  } else {
    if (initialHeight > maxSize) {
      finalHeight = maxSize;
      finalWidth = (finalHeight * initialWidth) / initialHeight;
    } else {
      return dataUrl;
    }
  }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = finalWidth;
  canvas.height = finalHeight;
  if (!ctx) {
    return dataUrl;
  }

  ctx.drawImage(image, 0, 0, finalWidth, finalHeight);
  return canvas.toDataURL("image/jpeg", 0.86);
};

const cloneFile = async (file: File): Promise<File> => {
  const buffer = await file.arrayBuffer();
  return new File([buffer], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
};

const getBase64 = async (
  file: Blob
): Promise<{ base64: string; type: string }> => {
  let sourceFile: Blob = file;

  if (isHeic(file)) {
    if (file.size > MAX_HEIC_SIZE_BYTES) {
      window.umami?.track("heic_too_big", {
        original_size: file.size,
        max_bytes: MAX_HEIC_SIZE_BYTES,
      });
      throw new FileTooBigError(MAX_HEIC_SIZE_BYTES, MAX_HEIC_SIZE_LABEL);
    }

    const startedAt = Date.now();
    const originalSize = file.size;
    window.umami?.track("heic_conversion_started", {
      original_size: originalSize,
      original_type: file.type || "unknown",
    });
    try {
      sourceFile = await convertHeicToJpeg(file);
      window.umami?.track("heic_conversion_success", {
        original_size: originalSize,
        converted_size: sourceFile.size,
        duration_ms: Date.now() - startedAt,
      });
    } catch (err) {
      window.umami?.track("heic_conversion_failed", {
        original_size: originalSize,
        duration_ms: Date.now() - startedAt,
        error: String((err as any)?.message || err).substring(0, 200),
      });
      throw new HeicConversionError(err);
    }
  }

  const dataUrl = await readAsDataURL(sourceFile);

  const isProcessableImage =
    typeof dataUrl === "string" &&
    (dataUrl.includes("image/png") ||
      dataUrl.includes("image/jpg") ||
      dataUrl.includes("image/jpeg"));

  const finalDataUrl = isProcessableImage
    ? await resizeImageIfNeeded(dataUrl)
    : dataUrl;

  return { base64: finalDataUrl, type: sourceFile.type };
};

const extractMimeFromBase64 = (base64: string | boolean): string => {
  if (typeof base64 !== "string") return "";
  const match = base64.match(/^data:([^;,]+)[;,]/);
  return match?.[1] || "";
};

const convertData = (data: DataType) => {
  let finalData: Array<FinalData> = [];

  const getType = (type: string) => {
    if (type === "Paragraph") {
      return "paragraph";
    } else if (type === "ButtonListPicture") {
      return "listPicture";
    } else if (type === "ButtonPicture" || type === "ButtonCamera") {
      return "picture";
    } else if (type === "ButtonPictureMask") {
      return "picture";
    } else if (type === "ButtonDocument") {
      return "document";
    } else if (type === "Textarea") {
      return "text";
    } else if (type === "YesNo") {
      return "radio";
    } else if (type === "Checkbox") {
      return "checkbox";
    } else if (type === "InputText") {
      return "text";
    } else if (type === "ButtonSignature") {
      return "signature";
    } else {
      return "";
    }
  };

  const getData = (type: string, value?: string, checked?: boolean) => {
    if (type === "Checkbox") {
      return checked;
    } else if (type === "YesNo") {
      if (checked === true) {
        return "1";
      } else if (checked === false) {
        return "2";
      } else {
        return "";
      }
    } else {
      return value;
    }
  };

  data.body.forEach((page) => {
    page.elements.forEach((element) => {
      let line: FinalData = {
        type: getType(element.type),
        name: element.text || element.title || element.placeholder || "",
        pdf: 0,
        inpdf: 1,
        data: "",
        fileType: "",
        time: element.time || "",
        type_document_true: element.type_true,
        type_document_false: element.type_false,
        special_file_type: element.isSpecialFileType,
      };

      line.data = getData(element.type, element.value, element.checked) || "";
      line.fileType = extractMimeFromBase64(line.data);

      const lines =
        element.type === "ButtonListPicture" &&
        Array.isArray(element.valueArray)
          ? element.valueArray.map((list: string) => ({
              ...line,
              data: list,
              fileType: extractMimeFromBase64(list),
            }))
          : [line];

      finalData.push(...lines);
    });
  });

  return finalData;
};

export { getBase64, convertData, isFileSupported, cloneFile };
